import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Polls /version.json and forces a fresh reload whenever the deployed build
 * version changes. Works alongside the service worker's update flow as a
 * belt-and-braces guarantee that PWA users never get stuck on a stale build.
 *
 * Behaviour:
 *  - Records the version observed at boot.
 *  - Polls every 60s while the tab is visible.
 *  - Also checks on tab-focus / visibility change.
 *  - On mismatch: shows a toast for 2s, then clears caches + reloads.
 */
export default function useAppVersionWatcher() {
  const initialRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    let timer = null;

    const fetchVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const j = await res.json();
        return j && j.version ? String(j.version) : null;
      } catch {
        return null;
      }
    };

    const triggerReload = async () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      toast.success('New version available — refreshing…', { duration: 1800 });
      try {
        // Best-effort cache wipe so even sticky HTTP caches let go.
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {/* ignore */}
      setTimeout(() => {
        // Hard reload (location.reload with no args fetches via SW; this
        // bypasses both HTTP cache and SW for the navigation).
        window.location.replace(window.location.pathname + window.location.search);
      }, 1800);
    };

    const check = async () => {
      const v = await fetchVersion();
      if (!v) return;
      if (initialRef.current == null) {
        initialRef.current = v;
        return;
      }
      if (v !== initialRef.current) {
        triggerReload();
      }
    };

    // Initial fetch on mount, then poll.
    check();
    timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, 60_000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);
}
